/**
 * Fetches an image URL and writes it to the system clipboard as image/png.
 *
 * Why this is more involved than `clipboard.write(fetch(url))`:
 *
 * - CORS: cdn.naumu.ai (marketing/landing images) serves no CORS headers, so a
 *   direct fetch() rejects. Those URLs are routed through the same-origin
 *   /cdn-media rewrite (next.config.ts). Attachment URLs 302 to signed R2 URLs
 *   which DO answer CORS - but only when the request carries an Origin header.
 *   An earlier <img> load (no Origin) can leave an ACAO-less copy in the HTTP
 *   cache that a later CORS fetch() is served from and rejects. `cache:
 *   'no-store'` sidesteps that intermittent failure.
 * - Safari only honours a clipboard write started synchronously inside the
 *   user gesture, so the Blob is handed to ClipboardItem as a Promise. If a
 *   browser rejects the promise-valued item but the bytes did arrive, we retry
 *   once with the concrete Blob.
 * - Chrome/Firefox accept only image/png; other formats are re-encoded through
 *   a canvas. Bytes are sniffed for the PNG signature because R2 may report
 *   `application/octet-stream`. SVG (no intrinsic bitmap) falls back from
 *   createImageBitmap to an <img> decode.
 *
 * Throws {@link CopyImageError} with a `reason` so callers can word the toast.
 */

export type CopyImageFailureReason = 'unsupported' | 'fetch' | 'convert' | 'clipboard';

export class CopyImageError extends Error {
	constructor(
		public readonly reason: CopyImageFailureReason,
		message: string,
		options?: { cause?: unknown },
	) {
		super(message, options);
		this.name = 'CopyImageError';
	}
}

/** User-facing message for a failed copy. Non-CopyImageError inputs get the generic line. */
export const describeCopyImageError = (err: unknown): string => {
	const reason = err instanceof CopyImageError ? err.reason : null;
	switch (reason) {
		case 'unsupported':
			return "This browser can't copy images to the clipboard";
		case 'fetch':
			return "Couldn't load the image to copy it";
		case 'clipboard':
			return 'Clipboard access was blocked - click the image and try again';
		default:
			return 'Could not copy image';
	}
};

const PNG_MIME = 'image/png';
const CDN_ORIGIN = 'https://cdn.naumu.ai';
const CDN_PROXY_PREFIX = '/cdn-media';

export const copyImageToClipboard = async (url: string): Promise<void> => {
	if (typeof navigator === 'undefined' || !navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
		throw new CopyImageError('unsupported', 'Async Clipboard API unavailable');
	}
	const supports = (ClipboardItem as { supports?: (type: string) => boolean }).supports;
	if (typeof supports === 'function' && !supports(PNG_MIME)) {
		throw new CopyImageError('unsupported', 'Clipboard does not accept image/png');
	}

	// Kick off the load synchronously (still inside the user gesture) and keep a
	// handle on its outcome so a clipboard failure can be told apart from a
	// fetch/convert failure without a second network round trip.
	const pngBlobPromise = fetchImageBlob(url).then(ensurePng);
	// Prevent an unhandled-rejection report if the clipboard write throws first.
	pngBlobPromise.catch(() => {});

	try {
		await navigator.clipboard.write([new ClipboardItem({ [PNG_MIME]: pngBlobPromise })]);
		return;
	} catch (writeErr) {
		// Surface the real cause when the bytes never arrived.
		const blob = await pngBlobPromise;
		// Bytes are fine, so the promise-valued item was the problem (or activation
		// timing). One retry with a concrete Blob covers browsers that only accept
		// resolved values.
		try {
			await navigator.clipboard.write([new ClipboardItem({ [PNG_MIME]: blob })]);
		} catch (retryErr) {
			throw new CopyImageError('clipboard', errorMessage(retryErr), { cause: writeErr });
		}
	}
};

/** Same-origin form of `url` where a direct fetch would be blocked by CORS. */
export const toFetchableImageUrl = (url: string): string =>
	url.startsWith(`${CDN_ORIGIN}/`) ? `${CDN_PROXY_PREFIX}${url.slice(CDN_ORIGIN.length)}` : url;

const fetchImageBlob = async (url: string): Promise<Blob> => {
	let res: Response;
	try {
		res = await fetch(toFetchableImageUrl(url), { cache: 'no-store' });
	} catch (err) {
		throw new CopyImageError('fetch', errorMessage(err), { cause: err });
	}
	if (!res.ok) throw new CopyImageError('fetch', `Image fetch failed with status ${res.status}`);
	const blob = await res.blob();
	if (blob.size === 0) throw new CopyImageError('fetch', 'Image fetch returned an empty body');
	return blob;
};

const ensurePng = async (blob: Blob): Promise<Blob> => {
	if (await isPng(blob)) {
		// Re-type so the clipboard sees exactly image/png (R2 may say octet-stream).
		return normalizedMime(blob) === PNG_MIME ? blob : new Blob([blob], { type: PNG_MIME });
	}
	try {
		return await convertBlobToPng(blob);
	} catch (err) {
		throw new CopyImageError('convert', errorMessage(err), { cause: err });
	}
};

const normalizedMime = (blob: Blob): string => blob.type.split(';')[0].trim().toLowerCase();

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const isPng = async (blob: Blob): Promise<boolean> => {
	if (normalizedMime(blob) === PNG_MIME) return true;
	if (blob.size < PNG_SIGNATURE.length) return false;
	const head = new Uint8Array(await blob.slice(0, PNG_SIGNATURE.length).arrayBuffer());
	return PNG_SIGNATURE.every((byte, i) => head[i] === byte);
};

const convertBlobToPng = async (blob: Blob): Promise<Blob> => {
	const { source, release } = await decodeImage(blob);
	const canvas = document.createElement('canvas');
	canvas.width = source.width;
	canvas.height = source.height;
	if (canvas.width === 0 || canvas.height === 0) {
		release();
		throw new Error('Image has no dimensions');
	}
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		release();
		throw new Error('Canvas 2D context unavailable');
	}
	ctx.drawImage(source, 0, 0);
	release();
	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((b) => {
			if (b) resolve(b);
			else reject(new Error('Canvas toBlob returned null'));
		}, PNG_MIME);
	});
};

interface DecodedImage {
	source: ImageBitmap | HTMLImageElement;
	/** Frees the bitmap / object URL once the pixels are on a canvas. */
	release: () => void;
}

/**
 * createImageBitmap is fastest and honours EXIF orientation, but rejects SVGs
 * without intrinsic size (and some odd encodings); an <img> decode is the
 * universal fallback.
 */
const decodeImage = async (blob: Blob): Promise<DecodedImage> => {
	if (typeof createImageBitmap === 'function') {
		try {
			const bitmap = await createImageBitmap(blob);
			return { source: bitmap, release: () => bitmap.close() };
		} catch {
			// fall through to <img>
		}
	}
	const objectUrl = URL.createObjectURL(blob);
	const release = () => URL.revokeObjectURL(objectUrl);
	try {
		const img = new Image();
		img.decoding = 'async';
		img.src = objectUrl;
		await img.decode();
		return { source: img, release };
	} catch (err) {
		release();
		throw err;
	}
};

const errorMessage = (err: unknown): string =>
	err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unknown error';
